import { useState, useEffect, RefObject, useCallback } from "react";

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

interface UseDropdownPositionOptions {
  itemHeight?: number;
  itemCount?: number;
  maxHeight?: number;
  offset?: number;
  padding?: number;
}

interface UseDropdownPositionReturn {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  open: () => void;
  position: DropdownPosition;
}

/**
 * Hook that manages dropdown positioning with viewport boundary checking
 * and auto-closing on resize/scroll
 */
export const useDropdownPosition = (
  buttonRef: RefObject<HTMLElement | null>,
  options: UseDropdownPositionOptions = {},
): UseDropdownPositionReturn => {
  const {
    itemHeight = 60,
    itemCount = 3,
    maxHeight = 192,
    offset = 4,
    padding = 16,
  } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition>({
    top: 0,
    left: 0,
    width: 0,
  });

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const dropdownHeight = Math.min(maxHeight, itemCount * itemHeight);

    // Calculate left position with boundary checking
    let left = rect.left;
    const maxLeft = viewportWidth - rect.width - padding;
    if (left < padding) {
      left = padding;
    } else if (left > maxLeft) {
      left = maxLeft;
    }

    // Calculate width - on mobile, match the button width but ensure it fits
    let width = rect.width;
    if (width > viewportWidth - padding * 2) {
      width = viewportWidth - padding * 2;
    }

    // Check if dropdown fits below, otherwise open above
    const spaceBelow = viewportHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    let top: number;

    if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
      // Open below
      top = rect.bottom + offset;
    } else {
      // Open above
      top = rect.top - dropdownHeight - offset;
    }

    // Ensure top doesn't go off screen
    if (top < padding) {
      top = padding;
    }

    setPosition({ top, left, width });
  }, [buttonRef, itemCount, itemHeight, maxHeight, offset, padding]);

  const toggle = useCallback(() => {
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen((prev) => !prev);
  }, [isOpen, calculatePosition]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    calculatePosition();
    setIsOpen(true);
  }, [calculatePosition]);

  // Close dropdown on resize/scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleResizeOrScroll = () => {
      setIsOpen(false);
    };

    window.addEventListener("resize", handleResizeOrScroll);
    window.addEventListener("scroll", handleResizeOrScroll, true);

    return () => {
      window.removeEventListener("resize", handleResizeOrScroll);
      window.removeEventListener("scroll", handleResizeOrScroll, true);
    };
  }, [isOpen]);

  return {
    isOpen,
    toggle,
    close,
    open,
    position,
  };
};
